import React, { useState, useCallback, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import useAxiosClient from "../../../axios-client";

/**
 * ReferenceListPane — the detail half of Product Settings (v3).
 *
 * One of these is on screen at a time, filling the pane beside the rail. It
 * carries a name-list and the three writes every such list needs: create,
 * rename, delete.
 *
 * The one structural idea worth knowing: **writes dock, they do not float.**
 * Create, rename and delete-confirm all render as a fixed strip between the
 * pane header and the scroller, never inline in the row. A popover pinned to a
 * row scrolls away from its own error the moment the list moves, and in a
 * thousand-row list it can be scrolled off screen entirely while still holding
 * unsaved text. Docked, the strip stays put, the row it refers to is marked
 * below, and the rest of the list dims.
 *
 * `serverSearch` is fabric colours: the search hits the server, the count reads
 * "N of TOTAL", a footer loads the next page, and there is no rename — the
 * server has never offered one.
 */
export default function ReferenceListPane({
  settingsKey,
  // The `settings/…` segment writes go to. Defaults to `settingsKey`, which
  // every list but one matches; fabric colours are keyed `fabricColours` for
  // their copy but have always been served under `settings/fabrics`.
  apiKey = settingsKey,
  title,
  copy = {},
  data,
  loading,
  fetch,
  search = "",
  onSearchChange,
  searchInputRef,
  serverSearch = false,
  totalCount,
  loadMore,
  hasMore,
}) {
  const axiosClient = useAxiosClient();

  // -- the docked write strip --------------------------------------------------
  // Exactly one write is open at a time, so they share a single slot rather
  // than three independent pieces of state that could each be half-open.
  const [write, setWrite] = useState(null); // {mode: 'create'|'rename'|'delete', id?, value?}
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const writeInputRef = useRef(null);

  const closeWrite = useCallback(() => {
    setWrite(null);
    setError(null);
    setSaving(false);
  }, []);

  // Switching lists must not leave another list's half-typed write on screen.
  useEffect(() => {
    closeWrite();
  }, [settingsKey, closeWrite]);

  useEffect(() => {
    if (write?.mode === "create" || write?.mode === "rename") {
      writeInputRef.current?.focus();
      writeInputRef.current?.select();
    }
  }, [write?.mode, write?.id]);

  // -- duplicate guard ---------------------------------------------------------
  // Checked here as well as on the server so the common mistake is answered
  // instantly and the offending row can be pointed at.
  const duplicateOf = useCallback(
    (value, excludeId = null) => {
      const norm = value.trim().toLowerCase();
      if (!norm) return null;
      return (
        data.find(
          (item) => item.name.trim().toLowerCase() === norm && item.id !== excludeId
        ) || null
      );
    },
    [data]
  );

  const duplicate =
    write && (write.mode === "create" || write.mode === "rename")
      ? duplicateOf(write.value, write.mode === "rename" ? write.id : null)
      : null;

  const errorText = (err, fallback) =>
    (err.response && err.response.data && err.response.data.message) ||
    err.message ||
    fallback;

  // -- create ------------------------------------------------------------------
  const submitCreate = async () => {
    const name = (write?.value || "").trim();
    if (!name || duplicate) return;
    setSaving(true);
    try {
      const res = await axiosClient.post(`/settings/${apiKey}/store`, { name });
      // Not every store route answers with a message — fabric colours hand back
      // the row itself — and an empty toast reads as a failure.
      toast.success(res.data?.message || `Added “${name}”`);
      fetch();
      // Adding one usually means adding several, so the strip stays open and
      // empty rather than making the user reach for Add again.
      setWrite({ mode: "create", value: "" });
      setError(null);
    } catch (err) {
      const msg = errorText(err, "Failed to create");
      toast.error(msg);
      setError(msg);
    } finally {
      setSaving(false);
      writeInputRef.current?.focus();
    }
  };

  // -- rename ------------------------------------------------------------------
  const submitRename = async () => {
    const name = (write?.value || "").trim();
    const original = data.find((i) => i.id === write.id);
    if (!name || duplicate) return;
    if (name === original?.name) return closeWrite();
    setSaving(true);
    try {
      const res = await axiosClient.put(`/settings/${apiKey}/update/${write.id}`, { name });
      toast.success(res.data.message);
      fetch();
      closeWrite();
    } catch (err) {
      const msg = errorText(err, "Failed to update");
      toast.error(msg);
      setError(msg);
      setSaving(false);
    }
  };

  // -- delete ------------------------------------------------------------------
  const submitDelete = async () => {
    setSaving(true);
    try {
      const res = await axiosClient.delete(`/settings/${apiKey}/delete/${write.id}`);
      toast.success(res.data.message);
      fetch();
      closeWrite();
    } catch (err) {
      // A refusal here is the useful case — the server knows what still
      // references the row — so it is shown in the strip, not just toasted away.
      setError(errorText(err, "Failed to delete"));
      setSaving(false);
    }
  };

  const onWriteKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      write.mode === "create" ? submitCreate() : submitRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeWrite();
    }
  };

  // -- filtering ---------------------------------------------------------------
  // Server-searched lists arrive already filtered; the rest filter in memory.
  const ft = search.trim().toLowerCase();
  const shown = serverSearch || !ft
    ? data
    : data.filter((item) => item.name.toLowerCase().includes(ft));

  const total = totalCount ?? data.length;
  const countText = serverSearch
    ? `${data.length.toLocaleString()} of ${total.toLocaleString()}`
    : ft
    ? `${shown.length} of ${total}`
    : `${total}`;

  const isEmpty = !loading && data.length === 0 && !ft;
  const noMatch = !loading && shown.length === 0 && ft;
  const target = write?.id ? data.find((i) => i.id === write.id) : null;

  return (
    <section className="ps-pane">
      {/* ---- header: title, count, one search box, one add ---- */}
      <header className="ps-pane-head">
        <div className="ps-pane-title">
          <span className="ps-pane-name">{title}</span>
          <span className="ps-pane-count">{countText}</span>
        </div>

        <div className="ps-pane-tools">
          <div className={`ps-search ${search ? "ps-search-active" : ""}`}>
            <span className="ps-search-icon" aria-hidden="true" />
            <input
              ref={searchInputRef}
              className="ps-search-input"
              type="text"
              placeholder={`Search ${title}`}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {serverSearch && loading ? (
              <span className="ps-search-status">searching…</span>
            ) : (
              !search && <span className="ps-search-kbd">/</span>
            )}
          </div>

          <button
            type="button"
            className="ps-btn-primary"
            onClick={() => setWrite({ mode: "create", value: "" })}
          >
            {copy.addLabel || `Add ${copy.singular || "item"}`}
          </button>
        </div>
      </header>

      {/* ---- the docked write strip ---- */}
      {write?.mode === "create" && (
        <div className="ps-strip">
          <div className="ps-strip-row">
            <input
              ref={writeInputRef}
              className={`ps-strip-input ${duplicate ? "ps-strip-input-error" : ""}`}
              placeholder={`Name the new ${copy.singular || "entry"}…`}
              value={write.value}
              onChange={(e) => setWrite({ ...write, value: e.target.value })}
              onKeyDown={onWriteKeyDown}
            />
            <button
              type="button"
              className="ps-btn-quiet"
              disabled={saving || !!duplicate || !write.value.trim()}
              onClick={submitCreate}
            >
              {saving ? "Saving" : "Save"}
              {saving ? <span className="ps-spinner" /> : <span className="ps-kbd">⏎</span>}
            </button>
            <button type="button" className="ps-btn-quiet" onClick={closeWrite}>
              Cancel
            </button>
          </div>
          {duplicate && (
            <div className="ps-strip-error">“{duplicate.name}” already exists in this list.</div>
          )}
          {error && !duplicate && <div className="ps-strip-error">{error}</div>}
        </div>
      )}

      {write?.mode === "rename" && (
        <div className="ps-strip">
          <div className="ps-strip-label">Renaming · {target?.name}</div>
          <div className="ps-strip-row">
            <input
              ref={writeInputRef}
              className={`ps-strip-input ps-strip-input-focus ${
                duplicate ? "ps-strip-input-error" : ""
              }`}
              value={write.value}
              onChange={(e) => setWrite({ ...write, value: e.target.value })}
              onKeyDown={onWriteKeyDown}
            />
            <button
              type="button"
              className="ps-btn-primary ps-btn-sm"
              disabled={saving || !!duplicate}
              onClick={submitRename}
            >
              {saving ? "Saving" : "Save"}
              {saving ? <span className="ps-spinner" /> : <span className="ps-kbd">⏎</span>}
            </button>
            <button type="button" className="ps-btn-quiet" onClick={closeWrite}>
              Esc
            </button>
          </div>
          {duplicate && (
            <div className="ps-strip-error">“{duplicate.name}” already exists in this list.</div>
          )}
          {error && !duplicate && <div className="ps-strip-error">{error}</div>}
        </div>
      )}

      {write?.mode === "delete" && (
        <div className="ps-strip ps-strip-danger">
          <div className="ps-strip-title">Delete “{target?.name}”?</div>
          <div className="ps-strip-body">
            It is removed from every picker that offers it. Records already saved
            keep the name they were given.
          </div>
          <div className="ps-strip-row">
            <button
              type="button"
              className="ps-btn-danger"
              disabled={saving}
              onClick={submitDelete}
            >
              {saving ? "Deleting…" : `Delete ${copy.singular || "entry"}`}
            </button>
            <button type="button" className="ps-btn-quiet" onClick={closeWrite}>
              Keep it
            </button>
          </div>
          {error && (
            <div className="ps-strip-refusal">
              <span className="ps-strip-refusal-dot" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}

      {/* ---- the one scroll region ---- */}
      <div className={`ps-scroll ${write ? "ps-scroll-dimmed" : ""}`}>
        {loading && data.length === 0 && (
          <div className="ps-skeleton" aria-hidden="true">
            {[38, 52, 29, 44, 34].map((w, i) => (
              <div className="ps-skeleton-row" key={i}>
                <span className="ps-skeleton-bar" style={{ width: `${w}%` }} />
              </div>
            ))}
          </div>
        )}

        {isEmpty && (
          <div className="ps-blank">
            <span className="ps-blank-mark" aria-hidden="true" />
            <div className="ps-blank-title">{copy.emptyTitle || `No ${title} yet`}</div>
            <div className="ps-blank-body">{copy.emptyBody}</div>
            <button
              type="button"
              className="ps-btn-primary"
              onClick={() => setWrite({ mode: "create", value: "" })}
            >
              {copy.addLabel || "Add the first one"}
            </button>
          </div>
        )}

        {noMatch && (
          <div className="ps-blank">
            <div className="ps-blank-title">Nothing matches “{search}”</div>
            <div className="ps-blank-body">
              {serverSearch
                ? `No colour on the server matches. Clear the search to browse them all.`
                : `${total} ${total === 1 ? "entry exists" : "entries exist"}. Clear the search to see them all.`}
            </div>
            <div className="ps-blank-actions">
              <button type="button" className="ps-btn-quiet" onClick={() => onSearchChange("")}>
                Clear search
              </button>
              <button
                type="button"
                className="ps-btn-primary"
                onClick={() => setWrite({ mode: "create", value: search.trim() })}
              >
                Add “{search.trim()}”
              </button>
            </div>
          </div>
        )}

        {!isEmpty &&
          !noMatch &&
          shown.map((item) => {
            const isTarget = write?.id === item.id;
            return (
              <div
                key={item.id}
                className={`ps-row ${isTarget ? "ps-row-target" : ""}`}
              >
                <span className="ps-row-name" title={item.name}>
                  {ft && !serverSearch ? (
                    <HighlightMatch text={item.name} query={ft} />
                  ) : (
                    item.name
                  )}
                </span>

                {/* Colours that name a shelf rather than a product — the order
                    form must never offer these, so they are marked here. */}
                {item.store_only ? <span className="ps-tag">STORE ONLY</span> : null}

                {isTarget ? (
                  <span className="ps-row-state">
                    {write.mode === "rename" ? "being renamed" : "being deleted"}
                  </span>
                ) : (
                  <>
                    {/* Colours cannot be renamed — the server offers no route. */}
                    {!serverSearch && (
                      <button
                        type="button"
                        className="ps-row-action"
                        onClick={() => setWrite({ mode: "rename", id: item.id, value: item.name })}
                      >
                        Rename
                      </button>
                    )}
                    <button
                      type="button"
                      className="ps-row-action ps-row-action-danger"
                      onClick={() => setWrite({ mode: "delete", id: item.id })}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            );
          })}

        {/* Paged lists say so at the foot rather than pretending to be whole. */}
        {serverSearch && hasMore && !noMatch && (
          <button type="button" className="ps-load-more" onClick={loadMore} disabled={loading}>
            {loading ? "Loading…" : `Load more — ${data.length.toLocaleString()} of ${total.toLocaleString()} shown`}
          </button>
        )}
      </div>
    </section>
  );
}

/** Highlights the first occurrence of `query` inside `text`. */
function HighlightMatch({ text, query }) {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="ps-mark">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}
